import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import './EmbeddedCompanyForm.css';
import astronautCongratulate from '../../assets/poses/astronout-standing-congratulate.svg';
import CompanyFormFields from '../../components/CompanyFormFields/CompanyFormFields';
import ButtonPrimary from '../../components/Buttons/ButtonPrimary/ButtonPrimary';

const EmbeddedCompanyForm = () => {
  const partnerToken = process.env.REACT_APP_PARTNER_TOKEN;

  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    email: '',
    phone: '',
    role: ''
  });

  const [utmParams, setUtmParams] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [authUrl, setAuthUrl] = useState('');

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const params = {};

    const utmKeys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'utm_term',
      'fbclid',
      'gclid',
      'wbraid',
      'gbraid'
    ];

    utmKeys.forEach(key => {
      const value = searchParams.get(key);
      if (value) {
        params[key] = value;
      }
    });

    setUtmParams(params);
  }, []);

  useEffect(() => {
    const sendHeightToParent = () => {
      if (window.parent !== window) {
        const height = document.documentElement.scrollHeight;
        window.parent.postMessage({
          type: 'resize',
          height: height
        }, '*');
      }
    };

    sendHeightToParent();
    window.addEventListener('resize', sendHeightToParent);

    const observer = new MutationObserver(sendHeightToParent);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true
    });

    return () => {
      window.removeEventListener('resize', sendHeightToParent);
      observer.disconnect();
    };
  }, [showSuccess, message]);

  const formatPhoneNumber = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 11);

    if (digits.length === 0) {
      return "";
    }

    const part1 = digits.slice(0, 2);
    const part2 = digits.slice(2, 7);
    const part3 = digits.slice(7, 11);

    let formatted = "";
    if (part1) formatted += `(${part1}`;
    if (part2) formatted += `) ${part2}`;
    if (part3) formatted += `-${part3}`;
    return formatted;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const currentDigits = value.replace(/\D/g, '');
      const previousDigits = formData.phone.replace(/\D/g, '');

      if (currentDigits.length < previousDigits.length) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      } else {
        const masked = formatPhoneNumber(value);
        setFormData(prev => ({
          ...prev,
          [name]: masked
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    if (!formData.companyName.trim()) {
      setMessage('Nome da empresa é obrigatório');
      setMessageType('error');
      return false;
    }

    if (!formData.name.trim()) {
      setMessage('Nome completo é obrigatório');
      setMessageType('error');
      return false;
    }

    if (!formData.email.trim()) {
      setMessage('E-mail é obrigatório');
      setMessageType('error');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage('E-mail inválido');
      setMessageType('error');
      return false;
    }

    if (!formData.phone.trim()) {
      setMessage('Telefone é obrigatório');
      setMessageType('error');
      return false;
    }

    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 11) {
      setMessage('Telefone deve ter pelo menos 11 dígitos');
      setMessageType('error');
      return false;
    }

    if (!formData.role) {
      setMessage('Departamento é obrigatório');
      setMessageType('error');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!partnerToken) {
      setMessage('Token de parceiro não configurado');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      const formattedPhone = `+55 (${phoneDigits.slice(0, 2)}) ${phoneDigits.slice(2, 3)} ${phoneDigits.slice(3, 7)}-${phoneDigits.slice(7, 11)}`;

      const companyData = {
        data: {
          type: 'companies',
          attributes: {
            name: formData.name.trim(),
            phone: formattedPhone,
            email: formData.email.trim(),
            company_name: formData.companyName.trim(),
            company_phone: formattedPhone,
            company_email: formData.email.trim(),
            role: formData.role.trim(),
            ...utmParams
          }
        }
      };

      const response = await api.post('/external/companies', companyData, {
        headers: {
          'Authorization': `Bearer ${partnerToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'formSubmissionSuccess',
          email: formData.email,
          type: 'company'
        });

        const { authUrl: responseAuthUrl } = response.data.data.attributes;

        setAuthUrl(responseAuthUrl);
        setMessage('Cadastro realizado com sucesso!');
        setMessageType('success');
        setShowSuccess(true);
      }

    } catch (error) {
      console.error('Erro ao cadastrar empresa:', error);

      if (error.response) {
        const { status, data } = error.response;

        let errorMessages = [];
        if (data && Array.isArray(data.errors) && data.errors.length > 0) {
          data.errors.forEach(err => {
            if (err.id === 'email' && err.title === 'Email já está em uso') {
              errorMessages.push('Este e-mail já está cadastrado em nossa plataforma.');
            } else if (err.id === 'phone' && err.title === 'Phone já está em uso') {
              errorMessages.push('Este telefone já está cadastrado em nossa plataforma.');
            } else {
              errorMessages.push(err.title || err.detail || 'Erro de validação');
            }
          });
        } else if (data && data.error) {
          errorMessages = [data.error];
        } else if (data && data.message) {
          errorMessages = [data.message];
        }

        switch (status) {
          case 400:
            setMessage(errorMessages.join('\n') || 'Erro de validação.');
            break;
          case 401:
            setMessage('Token de autenticação inválido ou expirado.');
            break;
          case 409:
            setMessage(errorMessages.join('\n') || 'Dados já cadastrados.');
            break;
          case 422:
            setMessage(errorMessages.join('\n') || 'Dados inválidos.');
            break;
          default:
            setMessage('Erro interno do servidor, tente novamente mais tarde.');
        }
      } else {
        setMessage('Erro de conexão. Verifique sua internet e tente novamente.');
      }

      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleAccessApp = () => {
    if (authUrl) {
      window.open(authUrl, '_blank');
    }
  };

  if (showSuccess) {
    return (
      <div className="embedded-company-form">
        <div className="success-screen">
          <div className="success-icon">
            <img src={astronautCongratulate} alt="Astronauta celebrando" />
          </div>
          <h2>Cadastro Realizado!</h2>
          <p>Parabéns! Seu cadastro foi realizado com sucesso na eComLovers.</p>
          {authUrl && (
            <div className="success-actions">
              <ButtonPrimary
                text="Acessar a App"
                onClick={handleAccessApp}
                fontSize="16px"
                fontWeight={700}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="embedded-company-form">
      <div className="form-header">
        <h2>Cadastro de Empresa</h2>
        <p>Preencha os dados da sua empresa para se cadastrar na eComLovers</p>
      </div>

      <form onSubmit={handleSubmit} className="company-form">
        <div className="form-section">
          <CompanyFormFields
            formData={formData}
            handleInputChange={handleInputChange}
            message={message}
            messageType={messageType}
          />
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="submit-btn"
            disabled={loading}
          >
            {loading ? 'Cadastrando...' : 'Cadastrar Empresa'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmbeddedCompanyForm;

